import { useUpdatePreferences } from "@/hooks/useUpdatePreferences";
import {
  readBrowserPreferences,
  resolvePreferences,
  type EffectivePreferences,
} from "@/lib/preferences";
import { useAuthSession } from "@/lib/session-context";
import type { UserPreferencesPatch } from "@/types/users";

/**
 * The signed-in user's preferences, every one filled in — for a component
 * whose output depends on one (a unit, guidance). Re-renders when they change.
 * Throws outside the shell (no session), same as `useAuthSession`.
 */
export function useEffectivePreferences(): EffectivePreferences {
  const session = useAuthSession();
  return resolvePreferences(session, readBrowserPreferences(session.user_id));
}

/**
 * The preferences and a way to change them, for the Preferences dialog.
 * `readOnly` while a super admin views as someone: that session refuses every
 * write, so the controls say so rather than fail.
 */
export function useUserPreferences() {
  const session = useAuthSession();
  const update = useUpdatePreferences();
  return {
    effective: resolvePreferences(session, readBrowserPreferences(session.user_id)),
    stored: session.preferences,
    save: (patch: UserPreferencesPatch) => update.mutate(patch),
    readOnly: session.impersonating,
  };
}
